import * as React from 'react';
// @ts-expect-error
import {DragSource as dragSource, ConnectDragPreview, ConnectDragSource} from 'react-dnd';

import {Button} from 'shells/button/button';
import {Card} from 'shells/card/card';
import {FlashBanner} from 'shells/flash-banner/flash-banner';

import {DashHeader} from '../dash-header';
import {DashObfuscation} from '../dash-obfuscation';

import styles from './dash.module.css';

type Props = {
    id: string;
    onRemove?: (id: string) => void;
    onDeleteDashConfirm?: (id: string) => void;
    isConfigurable: boolean;
    isPreview?: boolean;
    onConfigure?: (id: string) => void;
    title: string;
    subheader: string;
    error?: any;
    errorWasDismissed?: boolean;
    onDismissError: (id: string) => void;
    children: any;
    connectDragSource: ConnectDragSource;
    obfuscationMessage?: string;
    isRefreshingComponent?: boolean;
    onErrorRetry: (id: string) => void;
    isDeleteConfirming?: boolean;
};

export class DashComponent extends React.Component<Props> {
    render() {
        return (
            <Card hasBorder={Boolean(this.props.isPreview)}>
                <DashHeader
                    title={this.props.title}
                    subheader={this.props.subheader}
                    error={this.props.error}
                    isRefreshingComponent={this.props.isRefreshingComponent}
                    isDeleteConfirming={this.props.isDeleteConfirming}
                    onRemove={this.props.onRemove ? this.handleRemove : undefined}
                    onDeleteDashConfirm={
                        this.props.onDeleteDashConfirm ? this.handleDeleteDashConfirm : undefined
                    }
                    isConfigurable={this.props.isConfigurable}
                    onConfigure={this.props.onConfigure ? this.handleConfigure : undefined}
                    connectDragSource={this.props.connectDragSource}
                />
                <div className={styles['dash-content']}>
                    {this.props.error && !this.props.errorWasDismissed ? (
                        <div className={styles['error-container']}>
                            <FlashBanner type='error' onHidden={this.handleErrorHidden}>
                                {this.props.error.msg}
                                {this.props.error.status === '410' &&
                                this.props.onDeleteDashConfirm ? (
                                    <Button
                                        variant='text-primary'
                                        size='big'
                                        onClick={this.handleDeleteDashConfirm}
                                    >
                                        Remove card
                                    </Button>
                                ) : (
                                    <Button
                                        variant='text-primary'
                                        size='big'
                                        // @ts-expect-error
                                        onClick={this.handleErrorRetryClick}
                                    >
                                        Retry
                                    </Button>
                                )}
                            </FlashBanner>
                        </div>
                    ) : undefined}
                    {this.props.obfuscationMessage ? (
                        <DashObfuscation message={this.props.obfuscationMessage} />
                    ) : undefined}
                    {this.props.children}
                </div>
            </Card>
        );
    }

    handleConfigure = () => {
        if (this.props.onConfigure) {
            this.props.onConfigure(this.props.id);
        }
    };

    handleRemove = () => {
        if (this.props.onRemove) {
            this.props.onRemove(this.props.id);
        }
    };

    handleDeleteDashConfirm = () => {
        if (this.props.onDeleteDashConfirm) {
            this.props.onDeleteDashConfirm(this.props.id);
        }
    };

    handleErrorRetryClick = (e: React.SyntheticEvent, closeFlash: () => void) => {
        this.props.onErrorRetry(this.props.id);
        closeFlash();
    };

    handleErrorHidden = () => {
        this.props.onDismissError(this.props.id);
    };
}

const dashSource = {
    beginDrag(props: DashDraggableProps) {
        // Return the data describing the dragged item
        return {id: props.id, originalIndex: props.index};
    },

    // @ts-expect-error
    endDrag(props, monitor) {
        if (!monitor.didDrop()) {
            // Did not drop over a valid target, so put it back where it was
            props.onDashDrop({id: props.id, index: monitor.getItem().originalIndex});

            return;
        }
        const dropResult = monitor.getDropResult();
        props.onDashDrop({id: props.id, index: dropResult.index});
    },
} as const;

type DashDraggableProps = Props & {
    index: number;
    isDragging: boolean;
    connectDragPreview: ConnectDragPreview;
    onDashDrop: (arg1: {id: string; index: number}) => void;
};

export class DashDraggable extends React.Component<DashDraggableProps> {
    render() {
        if (this.props.isDragging) {
            // Hide the original dash when dragging
            return null;
        }

        return this.props.connectDragPreview(
            <div className={styles['dash-component-wrapper']}>
                <DashComponent {...this.props} />
            </div>
        );
    }
}

// @ts-expect-error
const ds = dragSource('dash', dashSource, (connect, monitor) => ({
    connectDragSource: connect.dragSource(),
    connectDragPreview: connect.dragPreview(),
    isDragging: monitor.isDragging(),
}));
export const Dash = ds(DashDraggable);
